/**
 * Filtros server-side sobre `values[fieldKey]` en `collection_records`.
 *
 * El cliente serializa un array `ValueFilter[]` a string JSON
 * (`JSON.stringify(...)`) y lo pasa como `args.valueFilters`. El
 * parser lo valida con cuidado (límite de cantidad, tamaño máximo,
 * tipo por filtro) y produce reglas que el `filterFn` del
 * `createStandardPaginatedQuery` aplica fila a fila.
 *
 * Coherente con el parser de `numericRanges` en
 * `convex/lib/utils/index.ts`: ambos viven fuera del validator de
 * Convex para mantenerlo simple (el validator solo conoce strings).
 *
 * El tipo `type` se valida contra los `custom_field.type` reales en
 * runtime, pero acá lo tratamos como uno de los 4 grupos soportados
 * (multi/equals/range/bool). Los tipos no soportados (relation,
 * formula, file, etc.) se rechazan en el parseo y nunca llegan al
 * filtro.
 */
export type ValueFilterType = "multi" | "equals" | "range" | "bool";

export interface ValueFilter {
  /** Key del custom field (`conformsToFieldKey`). */
  fieldKey: string;
  /** Tipo de comparación. */
  type: ValueFilterType;
  /** Para `multi` y `equals`: valores aceptados. */
  values?: string[];
  /** Para `range`: min/max (ambos opcionales). */
  min?: number;
  max?: number;
  /** Para `bool`: valor aceptado (true o false). */
  value?: boolean;
}

export const MAX_VALUE_FILTERS_PER_REQUEST = 16;
export const MAX_VALUE_FILTER_VALUES_PER_ITEM = 32;
const MAX_SERIALIZED_BYTES = 8 * 1024;

/**
 * Parsea `args.valueFilters` (string JSON del cliente) a un
 * array tipado. Devuelve `[]` si el input es null/undefined o
 * cualquier string vacío. Cualquier error de parseo o de schema
 * se registra en consola y devuelve `[]` — la query no falla por
 * un filtro mal formateado; simplemente lo ignora.
 */
export function parseValueFilters(
  raw: string | null | undefined,
): ValueFilter[] {
  if (!raw || typeof raw !== "string") return [];
  if (raw.length > MAX_SERIALIZED_BYTES) {
    console.warn(
      `parseValueFilters: input truncado, excede ${MAX_SERIALIZED_BYTES} bytes`,
    );
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    console.warn("parseValueFilters: JSON inválido", err);
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const items: unknown[] =
    parsed.length > MAX_VALUE_FILTERS_PER_REQUEST
      ? parsed.slice(0, MAX_VALUE_FILTERS_PER_REQUEST)
      : parsed;
  if (parsed.length > MAX_VALUE_FILTERS_PER_REQUEST) {
    console.warn(
      `parseValueFilters: input truncado, excede ${MAX_VALUE_FILTERS_PER_REQUEST} items`,
    );
  }
  const out: ValueFilter[] = [];
  for (const item of items) {
    const f = validateValueFilter(item);
    if (f) out.push(f);
  }
  return out;
}

function validateValueFilter(input: unknown): ValueFilter | null {
  if (!input || typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;
  const fieldKey = typeof obj.fieldKey === "string" ? obj.fieldKey : null;
  const type = obj.type as ValueFilterType;
  if (!fieldKey || fieldKey.length === 0 || fieldKey.length > 128) return null;
  if (!["multi", "equals", "range", "bool"].includes(type)) return null;
  const out: ValueFilter = { fieldKey, type };
  if (type === "multi" || type === "equals") {
    const values = Array.isArray(obj.values)
      ? obj.values
          .filter((v): v is string => typeof v === "string")
          .slice(0, MAX_VALUE_FILTER_VALUES_PER_ITEM)
      : [];
    if (values.length === 0) return null;
    out.values = values;
  } else if (type === "range") {
    const min = typeof obj.min === "number" ? obj.min : undefined;
    const max = typeof obj.max === "number" ? obj.max : undefined;
    if (min === undefined && max === undefined) return null;
    out.min = min;
    out.max = max;
  } else {
    if (typeof obj.value !== "boolean") return null;
    out.value = obj.value;
  }
  return out;
}

/**
 * Predicate que aplica un array `ValueFilter[]` sobre el campo
 * `values[fieldKey]` de un documento `collection_records`. Se usa
 * como `filterFn` del `createStandardPaginatedQuery`. Tipos de
 * field no soportados simplemente devuelven `false` en ese filtro
 * (la fila se descarta), porque desde el server ya no se puede
 * inferir el tipo.
 */
export function matchesValueFilters(
  values: Record<string, unknown> | undefined,
  filters: ValueFilter[],
): boolean {
  if (filters.length === 0) return true;
  for (const f of filters) {
    const raw = values?.[f.fieldKey];
    if (!matchesOne(raw, f)) return false;
  }
  return true;
}

function matchesOne(raw: unknown, f: ValueFilter): boolean {
  switch (f.type) {
    case "multi": {
      // El valor puede llegar como array (multi_select), string con
      // separador, o un único id (relations con select). Comparamos
      // como membership-set.
      const set = toStringSet(raw);
      if (!set) return false;
      return f.values!.some((v) => set.has(v));
    }
    case "equals": {
      const v = normalizeScalar(raw);
      return v !== undefined && f.values!.some((allowed) => allowed === v);
    }
    case "range": {
      const n = toNumber(raw);
      if (n === null) return false;
      if (f.min !== undefined && n < f.min) return false;
      if (f.max !== undefined && n > f.max) return false;
      return true;
    }
    case "bool":
      return typeof raw === "boolean" ? raw === f.value : false;
  }
}

function toStringSet(raw: unknown): Set<string> | null {
  if (Array.isArray(raw)) {
    return new Set(raw.filter((v) => v != null).map((v) => String(v)));
  }
  if (typeof raw === "string") {
    if (raw === "") return null;
    // Separador común para arrays serializados.
    return new Set(raw.split(",").map((s) => s.trim()).filter(Boolean));
  }
  if (raw == null) return null;
  return new Set([String(raw)]);
}

function normalizeScalar(raw: unknown): string | undefined {
  if (raw == null || raw === "") return undefined;
  return String(raw);
}

function toNumber(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
